import React, { FC, ReactNode } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';

import EditPencil from '@ad-scripts/presentation/components/common/icons/edit_pencil';
import TrashIcon from '@ad-scripts/presentation/components/common/icons/trash';
import { ClickableLabel } from 'src/presentation/components/pages/ivr/components/BaseNodeSelector';
import { Row } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';

type ScheduleShortPresentationProps = {
  'data-aid': string;
  context?: ReactNode;
  disabled?: boolean;
  isOutdated?: boolean;
  node?: { value: string; label: string };
  onDelete?: Function;
  onSelectNode?: Function;
  onSwitchToEditMode?: Function;
  timeZone: string;
};

export const ScheduleShortPresentation: FC<ScheduleShortPresentationProps> = ({
  'data-aid': dataAid,
  context,
  disabled,
  isOutdated,
  node,
  onDelete,
  onSelectNode,
  onSwitchToEditMode,
  timeZone,
}) => {
  const goToNode = () => onSelectNode && onSelectNode(get(node, 'value'));
  return (
    <Row>
      <ShortPresentationWithActionsContainer data-aid={`${dataAid}`}>
        <ShortPresentationContainer>
          <ShortPresentationBlock>
            <ShortPresentationLabel>Active during</ShortPresentationLabel>
            {isOutdated ? <OutdatedLabel data-aid={`${dataAid}-outdatedTag`}>Past Date</OutdatedLabel> : null}
            <ShortPresentationDates data-aid={`${dataAid}-content`}>{context}</ShortPresentationDates>
          </ShortPresentationBlock>
          {timeZone && (
            <ShortPresentationBlock>
              <ShortPresentationLabel>Time Zone</ShortPresentationLabel>
              <ShortPresentationDates data-aid={`${dataAid}-timeZone`}>{timeZone}</ShortPresentationDates>
            </ShortPresentationBlock>
          )}
          <ShortPresentationBlock>
            <NextNodeLabel>
              <ClickableLabel
                label="Next Node"
                onClick={goToNode}
                disabled={!get(node, 'label', '')}
                data-aid={`${dataAid}-nextNode-link`}
              />
            </NextNodeLabel>
            <ShortPresentationNextNode data-aid={`${dataAid}-nextNode`}>
              {get(node, 'label', '')}
            </ShortPresentationNextNode>
          </ShortPresentationBlock>
        </ShortPresentationContainer>

        {!disabled && (
          <ShortPresentationActions>
            <EditPencil data-aid={`${dataAid}-editButton`} width={16} onClick={onSwitchToEditMode} />
            <TrashIcon data-aid={`${dataAid}-deleteButton`} width={16} onClick={onDelete} />
          </ShortPresentationActions>
        )}
      </ShortPresentationWithActionsContainer>
    </Row>
  );
};

const ShortPresentationBlock = styled.div`
  line-height: 162%;
`;

const ShortPresentationActions = styled.div`
  align-items: center;
  min-width: 48px;
  padding: 16px 0;

  svg {
    margin-left: 5px;
    cursor: pointer;
  }
`;

const ShortPresentationWithActionsContainer = styled.div`
  display: flex;
  width: 100%;
`;

const ShortPresentationContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;

  & > div:not(:last-child) {
    margin-bottom: 6px;
  }
`;

const ShortPresentationLabel = styled.label`
  font-weight: bold;
  margin-right: 8px;
`;

const OutdatedLabel = styled.span`
  background-color: ${p => p.theme.colors.gray800};
  border-radius: 4px;
  color: ${p => p.theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
`;

const ShortPresentationDates = styled.div``;

const NextNodeLabel = styled.label`
  font-weight: bold;
`;

const ShortPresentationNextNode = styled.div`
  display: block;
  line-height: 162%;
`;
